import {
  wardrobeCompositionGarmentsApiV1InternalWardrobeCompositionGarmentsGet,
  type AdminWardrobeCompositionGarment as WireGarment,
} from '@closet-theory/api';
import { configureAdminApi, handleUnauthorized } from './auth';
import { errorDetail } from './operation-policy';

// The wire types mark defaulted lists optional; the modal reads them filled in.
export type AdminWardrobeCompositionGarment = Omit<WireGarment, 'colours' | 'occasions'> & {
  colours: string[];
  occasions: string[];
};

export type AdminWardrobeCompositionGarments = {
  matches: GarmentDrilldownMatch[];
  total: number;
  limit: number;
  offset: number;
  items: AdminWardrobeCompositionGarment[];
};

export type GarmentDrilldownMatch = {
  key:
    | 'colour'
    | 'season'
    | 'occasion'
    | 'category'
    | 'subcategory'
    | 'pattern'
    | 'material'
    | 'fit'
    | 'brand'
    | 'warmth'
    | 'versatility';
  value: string;
};

/** The garments behind one chart mark: every match must hold (AND). */
export async function fetchGarmentDrilldown(
  matches: GarmentDrilldownMatch[],
  offset = 0,
  limit = 24,
): Promise<AdminWardrobeCompositionGarments> {
  configureAdminApi();
  const result = await wardrobeCompositionGarmentsApiV1InternalWardrobeCompositionGarmentsGet({
    query: { match: matches.map(({ key, value }) => `${key}:${value}`), limit, offset },
  });
  handleUnauthorized(result.response?.status);
  if (result.error || !result.data) {
    throw new Error(errorDetail(result.error, 'Garments could not be loaded.'));
  }
  const data = result.data;
  return {
    matches,
    total: data.total,
    limit: data.limit,
    offset: data.offset,
    items: (data.items ?? []).map((item) => ({
      ...item,
      colours: item.colours ?? [],
      occasions: item.occasions ?? [],
    })),
  };
}
